import React from 'react'
import * as Request from '../../utilities/request'
import { Button, Col, Form, FormCheck, Modal, Row } from 'react-bootstrap'
import { PageStatus } from '../../constants/pageStatus'
import { Staff } from '../../../../back-end/common/staff'
import { AppContext } from '../../App'
import Select from 'react-select'
import { CustomOption, MenuListOverride, SelectOption } from '../../components/ui/Form/Select'
import { FormDate } from '../../components/ui/Form/Date'
import { Loading } from '../../components/ui/Loading/Loading'
import { ClientGroup } from '../../../../back-end/common/clientGroup'
import { Client } from '../../../../back-end/common/client'
import { Grade } from '../../../../back-end/common/grade'
import { XeroOrg } from '../../../../back-end/common/xeroOrg'
import { AccountXeroOrg } from '../../../../back-end/common/accountXeroOrg'
import { DateTime } from 'luxon'
import { FormSwitch } from '../../components/ui/Form/Switch'
import { Job } from '../../../../back-end/common/job'

const pollReportDownload = async (reportS3Path: string, filename: string) => {
	const fileBody = {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		s3Location: reportS3Path,
		operation: 'getObject',
		filename,
	}
	let fileS3URL = ''
	let sleep = 1000
	let totalSleep = sleep

	do {
		await Request.sleep(sleep)
		const sleepIncrement = Math.min(sleep + sleep, 30000) // Cap polling interval at 30 seconds
		sleep = sleepIncrement
		totalSleep += sleepIncrement
		const s3Res = await Request.post('s3/getpresignedurl', fileBody)
		if (s3Res.data.file.signedUrl) {
			fileS3URL = s3Res.data.file.signedUrl
		}
	} while (fileS3URL === '' && totalSleep <= 660000) // Max wait of 11 mins

	return fileS3URL
}

const downloadReport = (reportURL: string) => {
	const link = document.createElement('a')
	link.href = reportURL
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}

interface ReportEndpointParams {
	startDate?: string
	employeeStaffXpmIDs?: string[]
	endDate?: string
	xeroOrgID?: string
	partnerStaffXpmID?: string
	managerStaffXpmID?: string
	clientGroupXpmID?: string
	clientXpmID?: string
	gradeID?: string
	radioOption?: string
	wipFromDate?: string
	checkboxValue?: boolean
	jobIDs?: string[]
}

interface ReportSettings {
	title: string
	showStartDate?: boolean
	startDateLabel?: string
	startDateIsRequired?: boolean
	startDateDefault?: string
	startDateReadOnly?: boolean
	showEndDate?: boolean
	endDateLabel?: string
	endDateIsRequired?: boolean
	endDateDefault?: string
	showEmployeeFilter?: boolean
	employeeFilterLabel?: string
	showDepartmentFilter?: boolean
	showPartnerFilter?: boolean
	showJobFilter?: boolean
	partnerFilterLabel?: string
	showManagerFilter?: boolean
	showClientGroupFilter?: boolean
	showClientFilter?: boolean
	showEmployeeCategoryFilter?: boolean
	showRadioFilter?: boolean
	radioDefault?: string
	radioOptions?: [string, string]
	showWipFromDate?: boolean
	showCheckboxFilter?: boolean
	checkboxDefault?: boolean
	checkboxLabel?: string
	reportEndpoint: (reportEndpointParams: ReportEndpointParams) => string
}

interface ReportModalProps {
	reportSettings: ReportSettings
	onHide: () => void
}

const ReportModal = ({ onHide, reportSettings }: ReportModalProps) => {
	const { appState } = React.useContext(AppContext)
	const [pageStatus, setPageStatus] = React.useState<PageStatus>('Loading')
	const [errorMsg, setErrorMsg] = React.useState('')

	const [staff, setStaff] = React.useState<Staff[]>([])
	const [clientGroups, setClientGroups] = React.useState<ClientGroup[]>([])
	const [clients, setClients] = React.useState<Client[]>([])
	const [grades, setGrades] = React.useState<Grade[]>([])
	const [xeroOrgs, setXeroOrgs] = React.useState<XeroOrg[]>([])
	const [jobs, setJobs] = React.useState<Job[]>([])

	const staffOptions = staff.sort((a, b) => (a.name < b.name ? -1 : 1)).map((s) => ({ value: s.xpmID, label: s.name }))
	const clientGroupOptions = clientGroups.sort((a, b) => (a.name < b.name ? -1 : 1)).map((cg) => ({ value: cg.xpmID, label: cg.name }))
	const clientOptions = clients.sort((a, b) => (a.name < b.name ? -1 : 1)).map((c) => ({ value: c.xpmID, label: c.name }))
	const gradeOptions = grades.sort((a, b) => (a.name < b.name ? -1 : 1)).map((g) => ({ value: g.gradeID, label: g.name }))
	const xeroOrgOptions = xeroOrgs.sort((a, b) => (a.name! < b.name! ? -1 : 1)).map((xo) => ({ value: xo.xeroOrgID!, label: xo.name! }))

	const [wipFromDate, setWipFromDate] = React.useState(DateTime.now().minus({ years: 2 }).toISODate())
	const [startDate, setStartDate] = React.useState(reportSettings.startDateDefault)
	const [endDate, setEndDate] = React.useState(reportSettings.endDateDefault || '')
	const [employeeOptionsSelected, setEmployeeOptionsSelected] = React.useState<SelectOption<string>[]>([])
	const [departmentOptionSelected, setDepartmentOptionSelected] = React.useState<SelectOption<string> | null>(null)
	const [partnerOptionSelected, setPartnerOptionSelected] = React.useState<SelectOption<string> | null>(null)
	const [managerOptionSelected, setManagerOptionSelected] = React.useState<SelectOption<string> | null>(null)
	const [clientGroupOptionSelected, setClientGroupOptionSelected] = React.useState<SelectOption<string> | null>(null)
	const [clientOptionSelected, setClientOptionSelected] = React.useState<SelectOption<string> | null>(null)
	const [gradeOptionSelected, setGradeOptionSelected] = React.useState<SelectOption<string> | null>(null)
	const [radioOptionSelected, setRadioOptionSelected] = React.useState<string>(
		reportSettings.radioDefault || (reportSettings.radioOptions ? reportSettings.radioOptions[0] : '') || ''
	)
	const [checkboxValue, setCheckboxValue] = React.useState<boolean>(reportSettings?.checkboxDefault || false)
	const [jobOptionsSelected, setJobOptionsSelected] = React.useState<SelectOption<string>[]>([])

	const filteredJobs = jobs.filter((job) => {
		const matchesClientGroup = clientGroupOptionSelected ? job.client?.clientGroupXpmID === clientGroupOptionSelected.value : true
		const matchesClient = clientOptionSelected ? job.clientXpmID === clientOptionSelected.value : true
		return matchesClientGroup && matchesClient
	})

	const jobOptions = filteredJobs.sort((a, b) => (a.name! < b.name! ? -1 : 1)).map((j) => ({ value: j.jobID!, label: `${j.number} - ${j.name!}` }))

	React.useEffect(() => {
		const getData = async () => {
			const fetchStaff = reportSettings.showEmployeeFilter || reportSettings.showManagerFilter || reportSettings.showPartnerFilter

			const [staffRes, clientGroupRes, gradeRes, xeroOrgRes] = await Promise.all([
				fetchStaff ? Request.get(`staff`) : undefined,
				reportSettings.showClientGroupFilter ? Request.get(`clientgroup`) : undefined,
				reportSettings.showEmployeeCategoryFilter ? Request.get(`grade`) : undefined,
				reportSettings.showDepartmentFilter
					? Request.get(`organisation?where=AccountXeroOrgIsBilly==1&where=XeroOrgTenantType==ORGANISATION`)
					: undefined,
				reportSettings.showClientFilter
					? Request.getPagedData(`client`, 4000, (data) => setClients((clients) => [...(clients || []), ...(data as { clients: Client[] }).clients]))
					: undefined,
				reportSettings.showJobFilter
					? Request.getPagedData(`job`, 4000, (data) => setJobs((jobs) => [...(jobs || []), ...(data as { jobs: Job[] }).jobs]))
					: undefined,
			])

			if (staffRes) {
				setStaff(staffRes.data.staff)
			}
			if (clientGroupRes) {
				setClientGroups(clientGroupRes.data.clientGroups)
			}
			if (gradeRes) {
				setGrades(gradeRes.data.grades)
			}
			if (xeroOrgRes) {
				setXeroOrgs(xeroOrgRes.data.accountXeroOrgs.map((axo: AccountXeroOrg) => axo.xeroOrg))
			}

			setPageStatus('Ready')
		}

		setPageStatus('Loading')
		if (appState.authState.isLoggedIn) {
			getData()
		}
	}, [appState.authState, reportSettings])

	const onHideWrapper = () => {
		if (pageStatus === 'Ready') {
			onHide()
		}
	}
	React.useEffect(() => {
		setJobOptionsSelected([])
	}, [clientGroupOptionSelected, clientOptionSelected])

	const getReportData = async () => {
		setPageStatus('Submitting')

		const res = await Request.get(
			'billyreports' +
				reportSettings.reportEndpoint({
					startDate,
					employeeStaffXpmIDs: employeeOptionsSelected.map((opt) => opt.value),
					endDate,
					xeroOrgID: departmentOptionSelected?.value,
					partnerStaffXpmID: partnerOptionSelected?.value,
					managerStaffXpmID: managerOptionSelected?.value,
					clientGroupXpmID: clientGroupOptionSelected?.value,
					clientXpmID: clientOptionSelected?.value,
					gradeID: gradeOptionSelected?.value,
					radioOption: radioOptionSelected,
					wipFromDate,
					checkboxValue,
					jobIDs: jobOptionsSelected.map((opt) => opt.value),
				})
		)

		if (res.data.success) {
			const filename = `${startDate}${endDate ? `-${endDate}` : ''}_${reportSettings.title}.xlsx`.replaceAll(/\s+/g, '_')
			const reportS3Location: string = res.data.reportS3Path
			const reportS3URL = await pollReportDownload(reportS3Location, filename)
			if (!reportS3URL) {
				setErrorMsg('Error generating report, timed out.')
			} else {
				downloadReport(reportS3URL)
			}
		} else {
			setErrorMsg(res.data.message || 'Error generating report.')
		}

		setPageStatus('Ready')
	}

	if (pageStatus === 'Loading') {
		return (
			<Modal show={true} onHide={onHideWrapper}>
				<div className="d-flex align-items-center p-5">
					<Loading show />
				</div>
			</Modal>
		)
	}

	return (
		<Modal show={true} onHide={onHideWrapper}>
			<Modal.Header>
				<h3 className="mb-0">{reportSettings.title}</h3>
			</Modal.Header>
			<Modal.Body className="pt-0">
				{reportSettings.showDepartmentFilter && (
					<Row className="pt-3">
						<Col className="d-flex flex-column">
							<Form.Label>{'Department'}</Form.Label>
							<Select
								options={xeroOrgOptions}
								onChange={(option) => setDepartmentOptionSelected(option)}
								value={departmentOptionSelected}
								isDisabled={pageStatus !== 'Ready'}
								isClearable
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showPartnerFilter && (
					<Row className="pt-3">
						<Col className="d-flex flex-column">
							<Form.Label>{reportSettings.partnerFilterLabel || 'Partner'}</Form.Label>
							<Select
								options={staffOptions}
								onChange={(option) => setPartnerOptionSelected(option)}
								value={partnerOptionSelected}
								isDisabled={pageStatus !== 'Ready'}
								isClearable
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showManagerFilter && (
					<Row className="pt-3">
						<Col className="d-flex flex-column">
							<Form.Label>{'Manager'}</Form.Label>
							<Select
								options={staffOptions}
								onChange={(option) => setManagerOptionSelected(option)}
								value={managerOptionSelected}
								isDisabled={pageStatus !== 'Ready'}
								isClearable
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showClientGroupFilter && (
					<Row className="pt-3">
						<Col className="d-flex flex-column">
							<Form.Label>{'Client Group'}</Form.Label>
							<Select
								options={clientGroupOptions}
								onChange={(option) => setClientGroupOptionSelected(option)}
								value={clientGroupOptionSelected}
								isDisabled={pageStatus !== 'Ready'}
								isClearable
								components={{ Option: CustomOption, MenuList: MenuListOverride }}
								styles={{
									singleValue: (base, state) => ({
										// TS def for the base CSS properties is too broad, any cast to stop singleValue complaining
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										...(base as any),
										display: state.selectProps.menuIsOpen ? 'none' : 'block',
									}),
								}}
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showClientFilter && (
					<Row className="pt-3">
						<Col className="d-flex flex-column">
							<Form.Label>{'Client'}</Form.Label>
							<Select
								options={clientOptions}
								onChange={(option) => setClientOptionSelected(option)}
								value={clientOptionSelected}
								isDisabled={pageStatus !== 'Ready'}
								isClearable
								components={{ Option: CustomOption, MenuList: MenuListOverride }}
								styles={{
									singleValue: (base, state) => ({
										// TS def for the base CSS properties is too broad, any cast to stop singleValue complaining
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										...(base as any),
										display: state.selectProps.menuIsOpen ? 'none' : 'block',
									}),
								}}
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showEmployeeCategoryFilter && (
					<Row className="pt-3">
						<Col className="d-flex flex-column">
							<Form.Label>{'Employee Category'}</Form.Label>
							<Select
								options={gradeOptions}
								onChange={(option) => setGradeOptionSelected(option)}
								value={gradeOptionSelected}
								isDisabled={pageStatus !== 'Ready'}
								isClearable
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showEmployeeFilter && (
					<Row className="pt-3">
						<Col className="d-flex flex-column">
							<Form.Label>{reportSettings.employeeFilterLabel || 'Employee'}</Form.Label>
							<Select
								options={staffOptions}
								isMulti
								onChange={(options) => setEmployeeOptionsSelected(options.map((opt) => opt))} // Re-map due to readonly options
								value={employeeOptionsSelected}
								isDisabled={pageStatus !== 'Ready'}
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showJobFilter && (
					<Row className="pt-3">
						<Col className="d-flex flex-column">
							<Form.Label>{'Job'}</Form.Label>
							<Select
								options={jobOptions}
								isMulti
								onChange={(options) => setJobOptionsSelected(options.map((opt) => opt))}
								value={jobOptionsSelected}
								isDisabled={pageStatus !== 'Ready'}
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showStartDate && (
					<Row className="pt-3">
						<Col>
							<FormDate
								name={'startDate'}
								label={reportSettings.startDateLabel || 'From'}
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
								disabled={pageStatus !== 'Ready' || reportSettings.startDateReadOnly}
								required={reportSettings.startDateIsRequired}
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showEndDate && (
					<Row className="pt-3">
						<Col>
							<FormDate
								name={'endDate'}
								label={reportSettings.endDateLabel || 'To'}
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
								disabled={pageStatus !== 'Ready'}
								required={reportSettings.endDateIsRequired}
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showRadioFilter && (
					<Row className="pt-3">
						<Col>
							<FormCheck
								name="radio"
								type="radio"
								label={reportSettings.radioOptions ? reportSettings.radioOptions[0] : 'Yes'}
								value={reportSettings.radioOptions ? reportSettings.radioOptions[0] : 'Yes'}
								onChange={(e) => setRadioOptionSelected(e.target.value)}
								checked={radioOptionSelected === (reportSettings.radioOptions ? reportSettings.radioOptions[0] : 'Yes')}
								disabled={pageStatus !== 'Ready'}
							/>
							<FormCheck
								name="radio"
								type="radio"
								label={reportSettings.radioOptions ? reportSettings.radioOptions[1] : 'No'}
								value={reportSettings.radioOptions ? reportSettings.radioOptions[1] : 'No'}
								onChange={(e) => setRadioOptionSelected(e.target.value)}
								checked={radioOptionSelected === (reportSettings.radioOptions ? reportSettings.radioOptions[1] : 'No')}
								disabled={pageStatus !== 'Ready'}
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showWipFromDate && (
					<Row className="pt-3">
						<Col>
							<FormDate
								name={'wipFromDate'}
								label={'WIP From Date'}
								value={wipFromDate}
								onChange={(e) => setWipFromDate(e.target.value)}
								disabled={pageStatus !== 'Ready'}
							/>
						</Col>
					</Row>
				)}
				{reportSettings.showCheckboxFilter && (
					<Row className="pt-3">
						<Col>
							<FormSwitch
								name="checkbox"
								label={reportSettings?.checkboxLabel || ''}
								labelCol={'auto'}
								disabled={pageStatus !== 'Ready'}
								type="checkbox"
								checked={checkboxValue}
								onChange={() => setCheckboxValue((val) => !val)}
							/>
						</Col>
					</Row>
				)}
				<Row className="pt-3">
					<Col />
					<Col sm="auto">
						<Button
							variant="primary"
							onClick={getReportData}
							disabled={
								pageStatus !== 'Ready' || (reportSettings.startDateIsRequired && !startDate) || (reportSettings.endDateIsRequired && !endDate)
							}
						>
							{pageStatus === 'Submitting' ? <Loading show small /> : 'Generate Report'}
						</Button>
					</Col>
				</Row>
				{pageStatus === 'Submitting' && (
					<Row className="pt-3">
						<Col>
							<Form.Label>Note: Report generation can take some time, please be patient.</Form.Label>
						</Col>
					</Row>
				)}
				{errorMsg && (
					<Row className="pt-3">
						<Col>
							<span className="span-body-2 span-bold span-error">{errorMsg}</span>
						</Col>
					</Row>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Row>
					<Col />
					<Col sm="auto">
						<Button className="px-3 py-2" variant="secondary" onClick={() => onHideWrapper()}>
							Close
						</Button>
					</Col>
				</Row>
			</Modal.Footer>
		</Modal>
	)
}

export type { ReportSettings }
export default ReportModal
export { pollReportDownload, downloadReport }
